import { useState } from 'react'
import { ANNOTATION_CATEGORIES } from 'constants/annotation'
import { KrpanoRN } from 'utils/krpanoRN'

type Props = {
  onClose: () => void
}

export default function RNFilterPopup({ onClose }: Props) {
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    () => KrpanoRN.categories || ANNOTATION_CATEGORIES.map((c) => c.key)
  )
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(
    () => KrpanoRN.features || ['annotation', 'comment']
  )

  const toggleCategory = (cat: string) => {
    const next = selectedCategories.includes(cat)
      ? selectedCategories.filter((c) => c !== cat)
      : [...selectedCategories, cat]

    setSelectedCategories(next)
    queueMicrotask(() => {
      KrpanoRN.ApplyMarkerFilter(next, selectedFeatures)
    })
  }

  const toggleFeature = (feat: string) => {
    const next = selectedFeatures.includes(feat)
      ? selectedFeatures.filter((f) => f !== feat)
      : [...selectedFeatures, feat]

    setSelectedFeatures(next)
    queueMicrotask(() => {
      KrpanoRN.ApplyMarkerFilter(selectedCategories, next)
    })
  }

  return (
    <div
      className="absolute right-0 top-full mt-2 z-[1000] w-[340px] rounded-lg bg-white p-3 shadow-xl ring-1 ring-black/5 text-left"
      style={{ color: 'initial' }}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[11px] font-bold text-slate-500">
          表示中のカテゴリ・機能
        </div>
        <button
          type="button"
          className="text-slate-400 hover:text-slate-700 border-0 bg-transparent p-0 cursor-pointer"
          onClick={onClose}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-x"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      <div className="mb-1.5 text-[10px] font-bold tracking-wider text-slate-500">
        注釈カテゴリ（G/A/S/M/E）
      </div>

      <div className="grid grid-cols-2 gap-1">
        {ANNOTATION_CATEGORIES.map((cat) => {
          const isSelected = selectedCategories.includes(cat.key)

          return (
            <div
              key={cat.key}
              className="flex items-center justify-between gap-2 rounded px-1 py-1 hover:bg-slate-50 cursor-pointer"
              onClick={() => toggleCategory(cat.key)}
            >
              <div className="flex items-center gap-2">
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-full text-white shadow-inner"
                  style={{ backgroundColor: cat.colorHex }}
                >
                  <span
                    className="text-[12px] font-black"
                    style={{ fontFamily: 'Georgia, serif' }}
                  >
                    {cat.key}
                  </span>
                </span>
                <span className="text-[12px] text-slate-700">
                  {cat.shortLabel}
                </span>
              </div>
              <input
                type="checkbox"
                className="h-4 w-4 accent-sky-600 cursor-pointer"
                checked={isSelected}
                onChange={() => {}}
              />
            </div>
          )
        })}
      </div>

      <div className="mt-3 mb-1.5 text-[10px] font-bold tracking-wider text-slate-500">
        機能（分離表示）
      </div>

      <div className="space-y-1">
        {/* 注釈 */}
        <div
          className="flex items-center justify-between gap-2 rounded px-1 py-1 hover:bg-slate-50 cursor-pointer"
          onClick={() => toggleFeature('annotation')}
        >
          <div className="flex items-center gap-2">
            <span
              className="flex h-6 w-6 items-center justify-center rounded-md text-white"
              style={{ backgroundColor: 'rgb(14, 165, 233)' }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-circle-dot"
              >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="1" />
              </svg>
            </span>
            <span className="text-[12px] text-slate-700">注釈</span>
          </div>
          <input
            type="checkbox"
            className="h-4 w-4 cursor-pointer"
            checked={selectedFeatures.includes('annotation')}
            onChange={() => {}}
            style={{ accentColor: 'rgb(212, 154, 25)' }}
          />
        </div>

        {/* コメント */}
        <div
          className="flex items-center justify-between gap-2 rounded px-1 py-1 hover:bg-slate-50 cursor-pointer"
          onClick={() => toggleFeature('comment')}
        >
          <div className="flex items-center gap-2">
            <span
              className="flex h-6 w-6 items-center justify-center rounded-md text-white"
              style={{ backgroundColor: 'rgb(8, 145, 178)' }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-message-square-more"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                <path d="M8 10h.01" />
                <path d="M12 10h.01" />
                <path d="M16 10h.01" />
              </svg>
            </span>
            <span className="text-[12px] text-slate-700">コメント</span>
          </div>
          <input
            type="checkbox"
            className="h-4 w-4 cursor-pointer"
            checked={selectedFeatures.includes('comment')}
            onChange={() => {}}
            style={{ accentColor: 'rgb(212, 154, 25)' }}
          />
        </div>
      </div>
    </div>
  )
}
